'use client';

import {
  OfferData,
  SectionOffer,
  StepupInfo,
} from '@wwe-portal/entities/offer.type';
import { flattenContent } from '@wwe-portal/widget/store-revamp';
import { useCallback, useMemo } from 'react';
import { CATEGORY_IDS } from '@wwe-portal/web/utils/constants/offer';
import { useTranslations } from 'next-intl';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';

const parseSectionOffer = (offerSection: SectionOffer) => {
  const offers = offerSection.offers ?? [];
  const section: {
    offers: OfferData[];
    title: string;
    description: string;
    tooltipTitle?: string;
    sectionImage?: string;
    banner: undefined;
  } = {
    title: offerSection.section?.sectionTitle ?? '',
    description: offerSection.section?.sectionSubTitle ?? '',
    tooltipTitle: offerSection.section?.sectionTooltip ?? '',
    sectionImage: offerSection.section?.sectionTexture?.startsWith('http')
      ? offerSection.section.sectionTexture
      : undefined,
    offers,
    banner: undefined,
  };
  return section;
};

const parseSectionOffers = (offerSection: SectionOffer[]) => {
  const sections = offerSection.map((offer) => parseSectionOffer(offer));
  return { sections, offers: [] };
};

const getOfferContent = ({
  categories,
}: {
  categories?: {
    featured?: SectionOffer[] | undefined;
    rubies?: SectionOffer[] | undefined;
    daily?: SectionOffer[] | undefined;
    cashPacks?: SectionOffer[] | undefined;
    rewards?: SectionOffer[] | undefined;
    webCoins?: SectionOffer[] | undefined;
    special?: SectionOffer[] | undefined;
    webCoinDaily?: SectionOffer[] | undefined;
    tournament?: SectionOffer[] | undefined;
    clubCoins?: SectionOffer[] | undefined;
  };
}) => {
  const featuredOffers = parseSectionOffers(
    categories?.featured?.filter((section) => section.offers.length > 0) ?? []
  );
  const rubiesOffers =
    categories?.rubies?.flatMap((section) => section.offers) ?? [];
  const dailyOffers =
    categories?.daily?.flatMap((section) => section.offers) ?? [];
  const cashPacksOffers =
    categories?.cashPacks?.flatMap((section) => section.offers) ?? [];
  const rewardOffers =
    categories?.rewards?.flatMap((section) => section.offers) ?? [];
  const webCoinOffers =
    categories?.webCoins?.flatMap((section) => section.offers) ?? [];

  return {
    featuredOffers,
    dailyOffers,
    cashPacksOffers,
    rubiesOffers,
    rewardOffers,
    webCoinOffers,
  };
};

export const useStoreVirtualizedData = (payload: {
  categories?: {
    featured?: SectionOffer[] | undefined;
    rubies?: SectionOffer[] | undefined;
    daily?: SectionOffer[] | undefined;
    cashPacks?: SectionOffer[] | undefined;
    rewards?: SectionOffer[] | undefined;
    webCoins?: SectionOffer[] | undefined;
    special?: SectionOffer[] | undefined;
    webCoinDaily?: SectionOffer[] | undefined;
    tournament?: SectionOffer[] | undefined;
    clubCoins?: SectionOffer[] | undefined;
  };
  stepupAvailable: boolean;
  stepupInfo?: Partial<StepupInfo> | null;
}) => {
  const t = useTranslations('shop.categories');
  const getCategoryTitle = useCallback(
    (categoryId: string) => {
      return t(`category_${categoryId}`);
    },
    [t]
  );
  const { sm, xl3 } = useResponsive();
  return useMemo(() => {
    const { stepupAvailable, stepupInfo } = payload;
    const allData = getOfferContent(payload);
    const banner =
      stepupAvailable && stepupInfo
        ? {
            bannerUrl: stepupInfo.imageUrl ?? '',
            description: stepupInfo.description ?? '',
            endDate: stepupInfo.endDate ?? '',
          }
        : undefined;
    const contents = [
      {
        ...allData.featuredOffers,
        categoryTitle: getCategoryTitle(CATEGORY_IDS.FEATURED),
        categoryId: CATEGORY_IDS.FEATURED,
        banner,
        viewAs: 'text' as const,
      },
      {
        categoryTitle: getCategoryTitle(CATEGORY_IDS.DAILY),
        categoryId: CATEGORY_IDS.DAILY,
        offers: allData.dailyOffers,
        banner: undefined,
      },
      {
        categoryTitle: getCategoryTitle(CATEGORY_IDS.RUBIES),
        categoryId: CATEGORY_IDS.RUBIES,
        offers: allData.rubiesOffers,
        banner: undefined,
      },
      {
        categoryTitle: getCategoryTitle(CATEGORY_IDS.CASH_PACKS),
        categoryId: CATEGORY_IDS.CASH_PACKS,
        offers: allData.cashPacksOffers,
        banner: undefined,
      },
      {
        categoryTitle: getCategoryTitle(CATEGORY_IDS.REWARDS),
        categoryId: CATEGORY_IDS.REWARDS,
        offers: allData.rewardOffers,
        banner: undefined,
      },
      {
        categoryTitle: getCategoryTitle(CATEGORY_IDS.WEBCOINS),
        categoryId: CATEGORY_IDS.WEBCOINS,
        offers: allData.webCoinOffers,
        banner: undefined,
      },
    ].filter((content) => content.offers.length > 0 || 'sections' in content);
    return flattenContent(contents, { sm, xl3 });
  }, [payload, sm, xl3, getCategoryTitle]);
};
